import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { labelOf, colorOf } from "@/lib/statuses";
import { ArrowLeft, Truck, MapPin, FileText, Wrench } from "lucide-react";

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [fixers, setFixers] = useState([]);
  const [selectedFixer, setSelectedFixer] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/orders/${id}`).then((r) => {
      setOrder(r.data);
      setSelectedFixer(r.data.fixer_id);
    });
  }, [id]);

  useEffect(() => {
    load();
    api.get("/admin/fixers").then((r) => setFixers(r.data));
  }, [load]);

  const reassign = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${id}/assign`, { fixer_id: selectedFixer });
      toast.success("Verksted oppdatert");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const markPaidOut = async () => {
    try {
      await api.post(`/admin/payouts/${id}`);
      toast.success("Markert utbetalt");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!order) {
    return (
      <div className="tk-container py-20 text-slate-500" data-testid="admin-order-loading">
        Laster …
      </div>
    );
  }

  return (
    <div className="tk-container py-16 max-w-4xl" data-testid="admin-order-detail">
      <Link to="/admin" className="text-sm text-slate-500 hover:text-[#0B1C30] inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Tilbake til driftspanel
      </Link>
      <p className="tk-overline mt-6 mb-3 font-mono">#{order.id.slice(0, 8)}</p>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h1 className="text-3xl sm:text-4xl font-semibold">{order.product_name}</h1>
        <span className={`text-xs px-2 py-1 rounded-md ${colorOf(order.status)}`} data-testid="order-status-pill">
          {labelOf(order.status)}
        </span>
      </div>
      <p className="text-sm text-slate-500 mb-10">
        {order.service_name} · Kunde: {order.customer_name}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="tk-card">
          <p className="tk-overline mb-3 flex items-center gap-2"><Truck size={14} /> Levering</p>
          <p className="text-sm">{order.delivery_method === "shipping" ? "Bring-frakt" : "Selv-levering på verksted"}</p>
          {order.shipping_label_url && (
            <a href={order.shipping_label_url} target="_blank" rel="noreferrer" className="text-xs text-[#0B1C30] underline mt-2 inline-block" data-testid="shipping-label-link">
              Fraktetikett →
            </a>
          )}
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-3 flex items-center gap-2"><MapPin size={14} /> Verksted</p>
          <p className="text-sm mb-3">{order.fixer_display_name}</p>
          <label className="text-xs text-slate-500">Tildel på nytt</label>
          <div className="flex gap-2 mt-1">
            <select
              value={selectedFixer}
              onChange={(e) => setSelectedFixer(e.target.value)}
              className="tk-input !py-1.5 text-sm flex-1"
              data-testid="reassign-select"
            >
              {fixers.map((f) => (
                <option key={f.id} value={f.id}>{f.display_name}</option>
              ))}
            </select>
            <button
              onClick={reassign}
              disabled={busy || selectedFixer === order.fixer_id}
              className="tk-btn-primary !py-1.5 !px-3 text-xs"
              data-testid="reassign-btn"
            >
              Tildel
            </button>
          </div>
        </div>
      </div>

      {order.is_diagnostic && order.diagnostic_findings && (
        <div className="tk-card mb-6" data-testid="diagnostic-block">
          <p className="tk-overline mb-3 flex items-center gap-2"><FileText size={14} /> Diagnose</p>
          <p className="text-sm mb-1"><span className="text-slate-500">Symptomer:</span> {(order.symptoms || []).join(", ")}</p>
          <p className="text-sm mb-1"><span className="text-slate-500">Funn:</span> {order.diagnostic_findings}</p>
          <p className="text-sm mb-1"><span className="text-slate-500">Anbefalt:</span> {order.recommended_repair}</p>
          <p className="text-sm"><span className="text-slate-500">Tilleggspris:</span> kr {Number(order.recommended_price || 0).toFixed(0)}</p>
          {order.extra_payment_status === "paid" && (
            <p className="text-xs text-emerald-700 mt-2">✓ Tilleggsbeløp betalt</p>
          )}
        </div>
      )}

      <div className="tk-card mb-6">
        <p className="tk-overline mb-3 flex items-center gap-2"><Wrench size={14} /> Beløp</p>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Del</span><span>kr {Number(order.part_price).toFixed(0)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Arbeid</span><span>kr {Number(order.service_fee).toFixed(0)}</span>
        </div>
        <div className="tk-hairline my-2" />
        <div className="flex justify-between font-semibold">
          <span>Total</span><span>kr {Number(order.total_price).toFixed(0)}</span>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Betalingsmåte: {order.payment_method || "—"} · Status:{" "}
          {order.payment_status === "paid" ? "Betalt" : order.payment_status}
        </p>
      </div>

      {order.status === "completed" && (
        <button onClick={markPaidOut} className="tk-btn-accent" data-testid="mark-paid-out-btn">
          Marker som utbetalt til verksted
        </button>
      )}

      <p className="text-xs text-slate-400 mt-6">
        Opprettet {new Date(order.created_at).toLocaleString("nb-NO")}
      </p>
    </div>
  );
}
